import { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { SearchBox, SearchFilters } from '@/components/SearchBox';
import { ResultCard } from '@/components/ResultCard';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';
import medaiLogo from '@/assets/medai-cite-logo-new.png';
import { BookmarkPlus, Sparkles, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useLanguage } from '@/contexts/LanguageContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { ReformulateAssistant } from '@/components/ReformulateAssistant';
import { SourceFilter } from '@/components/SourceFilter';
import { Helmet } from 'react-helmet';

interface SearchResult {
  title: string;
  authors: string;
  year: string;
  journal: string;
  snippet: string;
  url: string;
  citation: string;
  pmid?: string;
  doi?: string;
  source: 'PubMed' | 'Semantic Scholar' | 'CrossRef';
  aiInsight?: string;
}

const Index = () => {
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [currentStatement, setCurrentStatement] = useState('');
  const [currentFilters, setCurrentFilters] = useState<SearchFilters>({});
  const [selectedSources, setSelectedSources] = useState<Array<'PubMed' | 'Semantic Scholar' | 'CrossRef'>>([
    'PubMed',
    'Semantic Scholar',
    'CrossRef',
  ]);
  const [showAuthError, setShowAuthError] = useState(false);
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { plan } = useSubscription();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session?.user) {
        navigate('/');
      } else {
        setUser(session.user);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!session?.user) {
        navigate('/');
      } else {
        setUser(session.user);
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (location.state) {
      const { statement, yearFrom, yearTo, studyType } = location.state as any;
      if (statement) {
        setCurrentStatement(statement);
        const filters: SearchFilters = {};
        if (yearFrom) filters.yearFrom = yearFrom;
        if (yearTo) filters.yearTo = yearTo;
        if (studyType) filters.studyType = studyType;
        setCurrentFilters(filters);
        handleSearch(statement, filters);
      }
    }
  }, [location.state]);

  const handleSearch = async (statement: string, filters: SearchFilters, randomize = false) => {
    // Check if user is logged in
    if (!user) {
      setShowAuthError(true);
      return;
    }

    setShowAuthError(false);
    
    // Check usage limits before searching (WITHOUT incrementing)
    if (user) {
      const { data: limitCheck, error: limitError } = await supabase.functions.invoke('check-usage-limits', {
        body: { action: 'search', should_increment: false },
      });

      if (limitError) {
        console.error('Error checking limits:', limitError);
      } else if (limitCheck && !limitCheck.canPerform) {
        toast({
          title: t.limitReached,
          description: limitCheck.message,
          variant: 'destructive',
          action: plan === 'free' ? (
            <Button variant="outline" size="sm" onClick={() => navigate('/pricing')}>
              {t.viewPlans}
            </Button>
          ) : undefined,
        });
        return;
      }
    }

    setCurrentStatement(statement);
    setCurrentFilters(filters);
    setIsLoading(true);
    setResults([]);

    // Show processing toast
    toast({
      title: t.searchProcessing,
      description: t.searchProcessingDescription,
    });

    try {
      const { data, error } = await supabase.functions.invoke('search-citations', {
        body: {
          statement,
          yearFrom: filters.yearFrom,
          yearTo: filters.yearTo,
          studyType: filters.studyType,
          language,
          randomize,
        },
      });

      if (error) {
        // Handle rate limiting errors
        if (error.message?.includes('429') || error.message?.includes('rate limit')) {
          toast({
            title: t.searchError,
            description: 'Has alcanzado el límite de búsquedas. Por favor, espera unos momentos e intenta de nuevo.',
            variant: "destructive",
          });
          return;
        }
        
        // Handle payment required errors
        if (error.message?.includes('402') || error.message?.includes('payment')) {
          toast({
            title: t.searchError,
            description: 'Se requiere actualizar tu plan para continuar usando esta función.',
            variant: "destructive",
          });
          return;
        }
        
        // Error in search - don't count this search
        toast({
          title: t.searchError,
          description: "Hi ha hagut un error amb la cerca. Torna-ho a provar, no s'ha descomptat cap intent.",
          variant: "destructive",
        });
        return;
      }

      if (data?.error) {
        // Error in search - don't count this search
        toast({
          title: t.searchError,
          description: "Hi ha hagut un error amb la cerca. Torna-ho a provar, no s'ha descomptat cap intent.",
          variant: "destructive",
        });
        return;
      }

      if (data.results && data.results.length > 0) {
        // SUCCESS: Now increment the search counter
        await supabase.functions.invoke('check-usage-limits', {
          body: { action: 'search', should_increment: true },
        });

        setResults(data.results);
        toast({
          title: t.searchCompleted,
          description: `${t.resultsFound} ${data.results.length} ${t.results}`,
        });
        
        // Auto-scroll to results section
        setTimeout(() => {
          const resultsSection = document.querySelector('section.container');
          if (resultsSection) {
            resultsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      } else {
        // No results but no error - optionally don't count
        toast({
          title: t.noResults,
          description: t.noResultsDescription,
        });
      }
    } catch (error) {
      console.error('Search error:', error);
      // Unexpected error - don't count this search
      toast({
        title: t.searchError,
        description: "Hi ha hagut un error amb la cerca. Torna-ho a provar, no s'ha descomptat cap intent.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSourceToggle = (source: 'PubMed' | 'Semantic Scholar' | 'CrossRef') => {
    setSelectedSources((prev) => {
      if (prev.includes(source)) {
        // Don't allow deselecting if it's the last one
        if (prev.length === 1) return prev;
        return prev.filter((s) => s !== source);
      } else {
        return [...prev, source];
      }
    });
  };

  // Calculate source counts
  const sourceCounts = useMemo(() => {
    return results.reduce((acc, result) => {
      acc[result.source] = (acc[result.source] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
  }, [results]);

  // Filter results based on selected sources
  const filteredResults = useMemo(() => {
    return results.filter((result) => selectedSources.includes(result.source));
  }, [results, selectedSources]);

  const handleSaveSearch = async () => {
    if (!user) {
      toast({
        title: t.loginRequired,
        description: t.loginRequiredDescription,
        variant: "destructive",
      });
      return;
    }

    if (!currentStatement) {
      toast({
        title: t.noSearchToSave,
        description: t.noSearchToSaveDescription,
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('saved_searches')
        .insert([{
          user_id: user.id,
          statement: currentStatement,
          study_type: currentFilters.studyType || null,
          year_from: currentFilters.yearFrom || null,
          year_to: currentFilters.yearTo || null,
        }]);

      if (error) throw error;

      toast({
        title: "La teva cerca s'ha desat correctament",
        description: "Pots veure-la a 'Les meves cerques'",
      });
    } catch (error) {
      console.error('Save search error:', error);
      toast({
        title: "No s'ha pogut desar la cerca",
        description: "Torna-ho a provar més tard",
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Cerca d'articles mèdics amb IA | MedAI Cite - Cites automàtiques</title>
        <meta name="description" content="Cerca articles mèdics amb IA mèdica. Genera cites automàtiques d'investigació mèdica de PubMed, Semantic Scholar i CrossRef. Eina professional per a investigadors." />
        <link rel="canonical" href="https://medai-cite.com/search" />
        
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://medai-cite.com/search" />
        <meta property="og:title" content="Cerca d'articles mèdics amb IA | MedAI Cite" />
        <meta property="og:description" content="Cerca articles mèdics amb IA mèdica. Genera cites automàtiques d'investigació mèdica de PubMed amb intel·ligència artificial." />
        <meta property="og:image" content="https://medai-cite.com/medai-cite-logo-og.png" />
        <meta property="og:site_name" content="MedAI Cite" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://medai-cite.com/search" />
        <meta name="twitter:title" content="Cerca d'articles mèdics amb IA" />
        <meta name="twitter:description" content="Cerca articles mèdics amb IA mèdica i genera cites automàtiques d'investigació mèdica." />
        <meta name="twitter:image" content="https://medai-cite.com/medai-cite-logo-og.png" />
      </Helmet>
      
      <div className="h-screen overflow-hidden bg-background flex flex-col">
        <Header />
        <ReformulateAssistant />
      
      <div className="flex-1 overflow-y-auto">
        {/* Hero Section */}
        <header className="relative bg-gradient-to-br from-primary/5 via-accent/30 to-background overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img 
              src="/hero-medical-1024w.webp" 
              alt="Medical research" 
              className="w-full h-full object-cover"
              width="1920"
              height="1080"
              fetchPriority="high"
              decoding="async"
              sizes="100vw"
              srcSet="/hero-medical-640w.webp 640w, /hero-medical-1024w.webp 1024w, /hero-medical-1536w.webp 1536w, /hero-medical-compressed.webp 1920w"
            />
          </div>
          
          <div className="relative container mx-auto px-4 py-16 md:py-20">
            <div className="flex items-center gap-3 justify-center mb-8">
              <img 
                src={medaiLogo}
                alt="MedAI Cite Logo" 
                className="h-16 w-16 md:h-20 md:w-20 object-contain"
                width="80"
                height="80"
                loading="eager"
                decoding="async"
              />
              <h1 className="text-4xl md:text-5xl font-bold text-foreground">
                {t.appTitle}
              </h1>
            </div>

            <p className="text-lg md:text-2xl text-muted-foreground text-center max-w-3xl mx-auto mb-10">
              {t.subtitle}
            </p>

            <SearchBox onSearch={handleSearch} isLoading={isLoading} />
            
            {/* Auth Error Message */}
            {showAuthError && (
              <div className="mt-6 max-w-4xl mx-auto">
                <Alert variant="destructive" className="bg-destructive/10 border-destructive/50">
                  <AlertCircle className="h-5 w-5" />
                  <AlertDescription className="ml-2">
                    <span className="font-medium">{t.authRequiredSearch} </span>
                    <Link 
                      to="/auth" 
                      className="underline hover:text-destructive-foreground font-semibold"
                    >
                      {t.authRequiredSearchLink}
                    </Link>
                    .
                  </AlertDescription>
                </Alert>
              </div>
            )}
          </div>
        </header>

        {/* Upgrade Banner for Free Plan */}
        {plan === 'free' && !user && (
          <section className="container mx-auto px-4 py-4">
            <Alert className="bg-gradient-to-r from-primary/10 to-accent/10 border-primary/20">
              <Sparkles className="h-5 w-5 text-primary" />
              <AlertDescription className="ml-2">
                <div className="flex items-center justify-between">
                  <span>
                    {t.unlockUnlimited}
                  </span>
                  <Button size="sm" onClick={() => navigate('/pricing')}>
                    {t.viewPlans}
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          </section>
        )}

        {/* Results Section */}
        {results.length > 0 && (
          <section className="container mx-auto px-4 py-12 pb-6">
            <div className="mb-8 flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-semibold mb-2">
                  {t.searchResults}
                </h2>
                <p className="text-muted-foreground">
                  {filteredResults.length} de {results.length} {results.length !== 1 ? t.articlesFoundPlural : t.articlesFound}
                </p>
              </div>
              {user && (
                <div className="flex gap-2">
                  <Button 
                    onClick={() => handleSearch(currentStatement, currentFilters, true)} 
                    variant="outline"
                    disabled={isLoading}
                  >
                    {t.findNewArticles}
                  </Button>
                  <Button onClick={handleSaveSearch} variant="outline">
                    <BookmarkPlus className="mr-2 h-4 w-4" />
                    {t.saveSearch}
                  </Button>
                </div>
              )}
            </div>

            {plan === 'free' || plan === 'plus' || plan === 'pro' ? (
              <SourceFilter
                sources={['PubMed', 'Semantic Scholar', 'CrossRef']}
                selectedSources={selectedSources}
                onSourceChange={handleSourceToggle}
                sourceCounts={sourceCounts}
              />
            ) : null}

            <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-2">
              {filteredResults.map((result, index) => (
                <ResultCard key={result.pmid || result.doi || `result-${index}`} {...result} />
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Footer fix */}
      <footer className="border-t">
        <div className="container mx-auto px-4 py-3">
          <p className="text-center text-xs text-muted-foreground">
            Powered by ActuaAI
          </p>
        </div>
      </footer>
      </div>
    </>
  );
};

export default Index;